import { zodResolver } from "@hookform/resolvers/zod"
import { useQuery } from "@tanstack/react-query"
import { Loader2 } from "lucide-react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import type { LocalMediaItem, MediaChanges } from "@/components/media/mediaUploadSection"
import { LeaveConfirmationModal } from "@/components/modals/LeaveConfirmationModal"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard"
import { useTranslation } from "@/language"
import { getAssetUrl } from "@/utils/assets"
import { CancelRitualDialog } from "../components/CancelRitualDialog"
import type { CourseModule } from "../components/CourseModulesSection"
import { RitualForm } from "../components/RitualForm"
import { useResendOrganizerInvite, useWithdrawOrganizerInvite } from "../hooks/useRitualInvites"
import { useUpdateRitual } from "../hooks/useRitualMutations"
import { useRitualOrganizers } from "../hooks/useRitualOrganizers"
import { type RitualFormData, ritualSchema } from "../schemas/ritual.schema"
import {
  getRitualById,
  type RitualCourseModule,
  type RitualOrganiser,
  type RitualTag,
  type RitualUpload,
  type UpdateRitualData,
} from "../services/ritual.service"
import { RITUAL_TYPE_OPTIONS, RitualTypeEnum, RitualVisibilityEnum } from "../types"
import type { Organizer } from "../utils/accordionValidation"
import { getDeselectedValuesForVisibility } from "../utils/privacyConfig"

interface SelectOption {
  value: string
  label: string
}

const parseJsonArray = (value: string | string[] | undefined | null): string[] => {
  if (!value) return []
  if (Array.isArray(value)) return value.map(v => String(v).toLowerCase())
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed.map((v: string) => String(v).toLowerCase()) : []
  } catch {
    return []
  }
}

interface TagWithId {
  id: string
  name: string
}

export function EditRitualScreen() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { t } = useTranslation("rituals")
  const currentUser = useAuthStore(state => state.user)

  const {
    data: ritual,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["ritual", id],
    queryFn: () => getRitualById(id || ""),
    enabled: !!id,
  })

  const {
    organizers: ritualOrganizers,
    isLoading: isLoadingOrganizers,
    hasNextPage,
    fetchNextPage,
  } = useRitualOrganizers({ ritualId: id || "" })

  const { resendOrganizerInvite, isResending } = useResendOrganizerInvite(id || "")
  const { withdrawOrganizerInvite, isWithdrawing } = useWithdrawOrganizerInvite(id || "")

  const form = useForm<RitualFormData>({
    resolver: zodResolver(ritualSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      title: "",
      type: RitualTypeEnum.IN_PERSON,
      visibility: RitualVisibilityEnum.PUBLIC,
      accessType: RitualVisibilityEnum.PUBLIC,
      dateTime: "",
      repeat: "none",
      repeatAt: "",
      repeatAtType: "DAYS",
      duration: "",
      address: "",
      meetingLink: "",
      steps: "",
      participationFee: "Free",
      price: "",
      feeDetails: "",
      minParticipantRequirement: false,
      minParticipants: "",
      registrationDeadline: "",
      description: "",
      whatToBring: "",
      whatYoullExperience: "",
      intention: "",
      websiteOrSocialMedia: "",
      tags: [],
      organizers: [],
      mediaFiles: { images: [], videos: [] },
      privacySettings: {
        visibility: ["everyone"],
        comment: ["owner"],
        edit: ["owner"],
        join: ["everyone"],
        invite: ["owner"],
        seeParticipantList: ["owner"],
      },
    },
  })

  const { setValue, reset, getValues, watch } = form

  // Warn before leaving with unsaved edits or an in-flight video upload.
  const [isVideoUploading, setIsVideoUploading] = useState(false)
  const unsavedGuard = useUnsavedChangesGuard(form.formState.isDirty || isVideoUploading)

  const repeatOptions: SelectOption[] = [
    { value: "none", label: t("repeat.options.none") },
    { value: "daily", label: t("repeat.options.daily") },
    { value: "weekly", label: t("repeat.options.weekly") },
    { value: "monthly", label: t("repeat.options.monthly") },
    { value: "yearly", label: "Yearly" },
    { value: "custom", label: t("repeat.options.custom") },
  ]

  const participationFeeOptions: SelectOption[] = [
    { value: "Free", label: t("createRitual.options.participationFee.free") },
    { value: "Paid", label: t("createRitual.options.participationFee.paid") },
    { value: "Donation-based", label: t("createRitual.options.participationFee.donation") },
  ]

  const [tags, setTags] = useState<string[]>([])
  const [originalTagsWithIds, setOriginalTagsWithIds] = useState<TagWithId[]>([])
  const [selectedIntentions, setSelectedIntentions] = useState<Array<{ id: string; name: string }>>(
    [],
  )
  const [privacySettings, setPrivacySettings] = useState({
    comment: ["owner"],
    edit: ["owner"],
    join: ["everyone"],
    invite: ["owner"],
    seeParticipantList: ["owner"],
  })

  const handleCommentChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, comment: options }))
      setValue("privacySettings.comment", options)
    },
    [setValue],
  )
  const handleEditChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, edit: options }))
      setValue("privacySettings.edit", options)
    },
    [setValue],
  )
  const handleJoinChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, join: options }))
      setValue("privacySettings.join", options)
    },
    [setValue],
  )
  const handleInviteChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, invite: options }))
      setValue("privacySettings.invite", options)
    },
    [setValue],
  )
  const handleSeeParticipantListChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, seeParticipantList: options }))
      setValue("privacySettings.seeParticipantList", options)
    },
    [setValue],
  )

  const prevVisibilityRef = useRef<RitualVisibilityEnum | null>(null)
  useEffect(() => {
    const currentVisibility = watch("visibility") as RitualVisibilityEnum
    if (!currentVisibility) return
    if (prevVisibilityRef.current !== currentVisibility) {
      prevVisibilityRef.current = currentVisibility
      const currentPrivacySettings = {
        comment: getValues("privacySettings.comment"),
        edit: getValues("privacySettings.edit"),
        join: getValues("privacySettings.join"),
        invite: getValues("privacySettings.invite"),
        seeParticipantList: getValues("privacySettings.seeParticipantList"),
      }
      const deselected = getDeselectedValuesForVisibility(currentVisibility, currentPrivacySettings)
      setPrivacySettings({
        comment: deselected.comment,
        edit: deselected.edit,
        join: deselected.join,
        invite: deselected.invite,
        seeParticipantList: deselected.seeParticipantList,
      })
      setValue("privacySettings.comment", deselected.comment)
      setValue("privacySettings.edit", deselected.edit)
      setValue("privacySettings.join", deselected.join)
      setValue("privacySettings.invite", deselected.invite)
      setValue("privacySettings.seeParticipantList", deselected.seeParticipantList)
    }
  }, [setValue, getValues, watch])

  const handleCourseVideoAccessChange = useCallback(
    (value: "ALL" | "SEQUENTIAL") => {
      setCourseVideoAccess(value)
      setValue("courseVideoAccess", value as RitualFormData["courseVideoAccess"])
    },
    [setValue],
  )

  const [organizers, setOrganizers] = useState<Organizer[]>([])
  const [originalOrganizers, setOriginalOrganizers] = useState<Organizer[]>([])
  const [isOrganizerModalOpen, setIsOrganizerModalOpen] = useState(false)
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const [organizersToRemove, setOrganizersToRemove] = useState<string[]>([])

  const [mediaChanges, setMediaChanges] = useState<MediaChanges | null>(null)
  const [_hasCoverImage, setHasCoverImage] = useState(false)

  const initialMedia = useMemo<LocalMediaItem[] | undefined>(() => {
    if (!ritual?.uploads) return undefined
    return ritual.uploads.map((u: RitualUpload) => {
      const src =
        u.filePath?.startsWith("http") || !u.filePath ? u.filePath : getAssetUrl(u.filePath)
      const mediaType = u.fileType?.startsWith("video/")
        ? "VIDEO"
        : u.fileType?.startsWith("audio/")
          ? "AUDIO"
          : "IMAGE"
      let thumbnailSrc: string | undefined
      if ((mediaType === "VIDEO" || mediaType === "AUDIO") && u.thumbnailUrl) {
        thumbnailSrc = u.thumbnailUrl.startsWith("http")
          ? u.thumbnailUrl
          : getAssetUrl(u.thumbnailUrl)
      }
      const title = u.title || u.fileName?.replace(/\.[^.]+$/, "") || null
      return {
        id: u.id,
        uploadId: u.id,
        src,
        mediaType,
        isCover: u.isCover,
        sortOrder: u.sortOrder ?? 0,
        title,
        videoTitle: title,
        thumbnailSrc,
        thumbnailUploadId: u.id,
      }
    })
  }, [ritual?.uploads])

  const [courseModules, setCourseModules] = useState<CourseModule[]>([])
  const [courseVideoAccess, setCourseVideoAccess] = useState<"ALL" | "SEQUENTIAL">("SEQUENTIAL")
  const [_preservedValues, setPreservedValues] = useState({ meetingLink: "", steps: "" })

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const updateRitualMutation = useUpdateRitual(id || "")

  useEffect(() => {
    if (ritual) {
      const dateTime = new Date(ritual.ritualTs)
      const year = dateTime.getFullYear()
      const month = String(dateTime.getMonth() + 1).padStart(2, "0")
      const day = String(dateTime.getDate()).padStart(2, "0")
      const hours = String(dateTime.getHours()).padStart(2, "0")
      const minutes = String(dateTime.getMinutes()).padStart(2, "0")
      const dateTimeString = `${year}-${month}-${day}T${hours}:${minutes}`

      if (ritual.courseModules && ritual.courseModules.length > 0) {
        const modules: CourseModule[] = ritual.courseModules.map(
          (cm: RitualCourseModule, index: number) => {
            const videoPreview = cm.upload?.filePath
              ? cm.upload.filePath.startsWith("http")
                ? cm.upload.filePath
                : getAssetUrl(cm.upload.filePath)
              : undefined
            const thumbnailPreview = cm.thumbnailUrl || undefined
            return {
              id: cm.id || `new-${Date.now()}-${index}`,
              uploadId: cm.upload?.id,
              title: cm.title || "",
              description: cm.description || "",
              duration: cm.duration || "",
              order: cm.orderIndex !== undefined ? cm.orderIndex : index,
              isFree: cm.isFree ?? true,
              videoPreview,
              thumbnailPreview,
              mediaType: cm.upload?.fileType?.startsWith("audio/") ? "AUDIO" : "VIDEO",
            }
          },
        )
        setCourseModules(modules)
      }

      const imageUploads = (ritual.uploads || []).filter((u: RitualUpload) =>
        u.fileType?.startsWith("image/"),
      )
      const videoUploads = (ritual.uploads || []).filter((u: RitualUpload) =>
        u.fileType?.startsWith("video/"),
      )

      const validImages = imageUploads.map((u: RitualUpload) => {
        const imageUrl = u.filePath?.startsWith("http") ? u.filePath : getAssetUrl(u.filePath)
        return { id: u.id, src: imageUrl, isCover: u.isCover || false, existingId: u.id }
      })
      const validVideos = videoUploads.map((v: RitualUpload) => {
        const videoUrl = v.filePath?.startsWith("http") ? v.filePath : getAssetUrl(v.filePath)
        return { id: v.id, src: videoUrl, existingId: v.id }
      })

      const mediaData = { images: validImages, videos: validVideos }
      const tagsWithIds =
        ritual.tags?.map(t => ({
          id: t.id,
          name: (t as RitualTag & { tagText?: string }).tagText || t.name,
        })) || []
      const tagNames = tagsWithIds.map(t => t.name).filter((n): n is string => n != null)

      const ritualIntentions = ritual.intentions || []
      setSelectedIntentions(ritualIntentions.map(i => ({ id: i.id, name: i.name })))
      setValue(
        "intentionIds",
        ritualIntentions.map(i => i.id),
      )

      const orgData = (ritual.organisers || []).map((org: RitualOrganiser) => {
        if (org.type === "user") {
          return {
            id: org.organiserId,
            type: org.type as "user" | "community",
            name: org.user?.name || t("createRitual.fallback.noName"),
            identifier: org.user?.username || null,
            avatar: org.user?.coverImage || org.user?.profilePictureUrl || undefined,
            roleId: "host",
            roleName: t("createRitual.roleHost"),
          }
        } else {
          return {
            id: org.organiserId,
            type: org.type as "user" | "community",
            name: org.community?.name || t("createRitual.fallback.community"),
            identifier: org.community?.handle || null,
            avatar: org.community?.coverImage || undefined,
            roleId: "host",
            roleName: t("createRitual.roleHost"),
          }
        }
      })

      reset({
        title: ritual.title || "",
        type: ritual.ritualType || RitualTypeEnum.IN_PERSON,
        visibility: (ritual.accessType as RitualVisibilityEnum) || RitualVisibilityEnum.PUBLIC,
        accessType: ritual.accessType || RitualVisibilityEnum.PUBLIC,
        dateTime: dateTimeString,
        repeat: ritual.repeat?.toLowerCase() || "none",
        duration: ritual.duration ? String(ritual.duration) : "",
        address: ritual.streetAddress || "",
        city: ritual.city || "",
        state: ritual.state || "",
        country: ritual.country || "",
        lat: ritual.lat != null ? Number(ritual.lat) : undefined,
        long: ritual.long != null ? Number(ritual.long) : undefined,
        meetingLink: ritual.meetingLink || "",
        steps: ritual.steps || "",
        participationFee: ritual.isDonationBased
          ? "Donation-based"
          : ritual.isPaid
            ? "Paid"
            : "Free",
        price: ritual.fee != null ? String(ritual.fee) : "",
        feeDetails: ritual.feeDetails || "",
        description: ritual.description || "",
        whatToBring: ritual.whatToBring || "",
        whatYoullExperience: ritual.experienceDetails || "",
        intention: ritual.intention || "",
        intentionIds: ritualIntentions.map(i => i.id),
        websiteOrSocialMedia: ritual.websiteLink || "",
        tags: tagNames,
        organizers: orgData,
        mediaFiles: mediaData,
        privacySettings: {
          visibility: parseJsonArray(ritual.whoCanSeeYourRitual) || ["everyone"],
          comment: parseJsonArray(ritual.whoCanComment) || ["owner"],
          edit: parseJsonArray(ritual.whoCanEditRitual) || ["owner"],
          join: parseJsonArray(ritual.whoCanJoin) || ["everyone"],
        },
        minParticipantRequirement: ritual.activateMinimumParticipantRequirement || false,
        minParticipants: ritual.minParticipants ? String(ritual.minParticipants) : "",
        registrationDeadline: ritual.registrationDeadline
          ? typeof ritual.registrationDeadline === "string"
            ? ritual.registrationDeadline.split("T")[0]
            : ritual.registrationDeadline.toISOString().split("T")[0]
          : "",
      })

      setTags(tagNames)
      setOriginalTagsWithIds(tagsWithIds)
      setOrganizers(orgData)
      setOriginalOrganizers(orgData)
      setValue("mediaFiles", mediaData)

      // Media is managed by RitualMediaSection (session-style). Images +
      // audio go through the batched RitualMedia path; native video goes
      // through the Cloudflare pipeline (useNativeRitualVideos, issue #362)
      // but renders as a card in the same unified grid. The legacy
      // YouTube/Vimeo URL editor was removed (PR #423).

      // Set privacy settings - from ritual object (JSON strings that need parsing)
      const privacy = {
        visibility: parseJsonArray(ritual.whoCanSeeYourRitual) || ["everyone"],
        comment: parseJsonArray(ritual.whoCanComment) || ["owner"],
        edit: parseJsonArray(ritual.whoCanEditRitual) || ["owner"],
        join: parseJsonArray(ritual.whoCanJoin) || ["everyone"],
        invite: parseJsonArray(ritual.whoCanInvite) || ["owner"],
        seeParticipantList: parseJsonArray(ritual.whoCanSeeParticipantList) || ["owner"],
      }
      setPrivacySettings(privacy)
      setValue("privacySettings.join", privacy.join)

      // Set course video access control
      setCourseVideoAccess(ritual.courseVideoAccess === "ALL" ? "ALL" : "SEQUENTIAL")
    }
  }, [ritual, reset, setValue, t])

  const handleAddTag = (tag: string) => {
    const newTags = [...tags, tag]
    setTags(newTags)
    setValue("tags", newTags)
  }

  const handleRemoveTag = (tagToRemove: string) => {
    const updatedTags = tags.filter(tag => tag !== tagToRemove)
    setTags(updatedTags)
    setValue("tags", updatedTags)
  }

  const handleAddOrganizers = (
    newOrganizers: {
      id: string
      type: "user" | "community"
      name?: string | null
      identifier?: string | null
      profilePictureUrl?: string | null
      inviteNote?: string
      dataCount?: { followers?: number; members?: number }
    }[],
  ) => {
    const organizersWithType: Organizer[] = newOrganizers.map(org => ({
      id: org.id,
      type: org.type,
      name: org.name || "",
      identifier: org.identifier ?? null,
      avatar: org.profilePictureUrl || undefined,
      roleId: "host",
      roleName: t("createRitual.roleHost"),
      inviteNote: org.inviteNote,
    }))
    const updated = [...organizers, ...organizersWithType]
    setOrganizers(updated)
    setValue("organizers", updated)
    setIsOrganizerModalOpen(false)
  }

  const handleMarkOrganizerForRemoval = (organizerId: string) => {
    setOrganizersToRemove(prev => {
      if (prev.includes(organizerId)) return prev
      return [...prev, organizerId]
    })
  }

  // Handle media changes from RitualMediaSection (unified, session-style)
  const handleMediaChange = useCallback(
    (changes: MediaChanges) => {
      setMediaChanges(changes)
      // Keep the zod-validated mediaFiles field in sync with the live media so
      // an existing ritual's loaded image satisfies mediaFiles.images.min(1).
      // (A comment elsewhere claimed this "main sync" happened, but it never
      // actually set mediaFiles.)
      const images = changes.mediaItems.filter(i => i.mediaType === "IMAGE")
      const videos = changes.mediaItems.filter(i => i.mediaType === "VIDEO")
      setValue("mediaFiles", { images, videos }, { shouldValidate: false })
    },
    [setValue],
  )

  const handleCoverImageChange = useCallback((hasCover: boolean) => {
    setHasCoverImage(hasCover)
  }, [])

  const handleAddModule = () => {
    const newModule: CourseModule = {
      id: `new-${Date.now()}-${Math.random()}`,
      title: "",
      description: "",
      duration: "",
      order: courseModules.length,
      isFree: true,
    }
    setCourseModules([...courseModules, newModule])
  }

  const handleRemoveModule = (moduleId: string) => {
    const updated = courseModules.filter(m => m.id !== moduleId)
    const reordered = updated.map((module, index) => ({ ...module, order: index }))
    setCourseModules(reordered)
  }

  const handleReorderModules = (modules: CourseModule[]) => {
    setCourseModules(modules)
  }

  const handleUpdateModule = (
    moduleId: string,
    fieldOrUpdates: keyof CourseModule | Partial<CourseModule>,
    value?: string | boolean | File,
  ) => {
    const updated = courseModules.map(module => {
      if (module.id === moduleId) {
        if (typeof fieldOrUpdates === "string") return { ...module, [fieldOrUpdates]: value }
        return { ...module, ...fieldOrUpdates }
      }
      return module
    })
    setCourseModules(updated)
  }

  // Helper to convert privacy setting values to API format
  const toApiFormat = (values: string[]): string[] => {
    if (values.length === 0) return ["everyone"]
    return values.map(v => {
      const lower = v.toLowerCase()
      const mapping: Record<string, string> = {
        everyone: "everyone",
        owner: "owner",
        organisers: "organizers",
        organizers: "organizers",
        participants: "participants",
        anyone: "everyone",
        followers: "followers",
        community_members: "community_members",
        invite_req: "invite_req",
      }
      return mapping[lower] || lower
    })
  }

  const handleUpdate = async (data: RitualFormData) => {
    if (!id) return
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const durationMinutes = data.duration ? parseInt(data.duration, 10) : 30
      const addedTags = tags.filter(t => !originalTagsWithIds.map(ot => ot.name).includes(t))
      const removedTagIds = originalTagsWithIds
        .filter(ot => !tags.includes(ot.name))
        .map(ot => ot.id)
      const addedOrganizers = organizers.filter(o => !originalOrganizers.find(oo => oo.id === o.id))
      const removedOrganizers = originalOrganizers
        .filter(o => !organizers.find(no => no.id === o.id))
        .map(o => ritual?.organisers?.find((ro: RitualOrganiser) => ro.organiserId === o.id)?.id)
        .filter(Boolean) as string[]

      const [datePart, timePart] = (data.dateTime || "").split("T")
      const [year, month, day] = datePart.split("-").map(Number)
      const [hours, minutes] = timePart.split(":").map(Number)
      const localDate = new Date(year, month - 1, day, hours, minutes)
      const localIsoTime = localDate.toISOString()

      const isPersonal = data.visibility === RitualVisibilityEnum.PERSONAL

      const updateData: Partial<UpdateRitualData> = {
        title: data.title,
        type: data.type,
        accessType: data.accessType as RitualVisibilityEnum,
        date: localIsoTime,
        repeat: data.repeat,
        repeatAt: data.repeat === "custom" ? data.repeatAt : undefined,
        repeatAtType: data.repeat === "custom" ? data.repeatAtType : undefined,
        duration: durationMinutes,
        description: data.description || undefined,
        intention: data.intention || undefined,
        intentionIds: data.intentionIds && data.intentionIds.length > 0 ? data.intentionIds : [],
        meetingLink:
          data.type === RitualTypeEnum.VIRTUAL_COURSE ? data.meetingLink || undefined : undefined,
        steps: data.type === RitualTypeEnum.SELF_GUIDED ? data.steps || undefined : undefined,
      }

      if (!isPersonal) {
        updateData.streetAddress = data.address || undefined
        updateData.city = data.city || undefined
        updateData.state = data.state || undefined
        updateData.country = data.country || undefined
        updateData.lat = data.lat
        updateData.long = data.long
        updateData.participationFee = data.participationFee
        if (data.type === RitualTypeEnum.SELF_GUIDED_COURSE) {
          updateData.courseVideoAccess = courseVideoAccess
        }
        updateData.price = data.price ? parseFloat(data.price) : undefined
        updateData.feeDetails = data.feeDetails || undefined
        updateData.whatToBring = data.whatToBring || undefined
        updateData.experienceDetails = data.whatYoullExperience || undefined
        updateData.webOrSocialMediaLink = data.websiteOrSocialMedia || undefined
      }

      if (addedTags.length > 0 || removedTagIds.length > 0) {
        updateData.tags = {
          add: addedTags.length > 0 ? addedTags : undefined,
          remove: removedTagIds.length > 0 ? removedTagIds : undefined,
        }
      }

      if (addedOrganizers.length > 0 || removedOrganizers.length > 0) {
        updateData.organizers = {
          add:
            addedOrganizers.length > 0
              ? addedOrganizers.map(o => ({
                  type: o.type as "user" | "community",
                  id: o.id,
                  inviteNote: o.inviteNote,
                }))
              : undefined,
          remove:
            removedOrganizers.length > 0 || organizersToRemove.length > 0
              ? [...removedOrganizers, ...organizersToRemove]
              : undefined,
        }
      }

      if (isPersonal) {
        updateData.whoCanSeeYourRitual = ["owner"]
        updateData.whoCanComment = ["owner"]
        updateData.whoCanEditRitual = ["owner"]
        updateData.whoCanJoin = ["invite_req"]
        updateData.whoCanInvite = ["owner"]
        updateData.whoCanSeeParticipantList = ["owner"]
      } else {
        updateData.whoCanSeeYourRitual = toApiFormat(data.privacySettings.visibility)
        updateData.whoCanComment = toApiFormat(data.privacySettings.comment)
        updateData.whoCanEditRitual = toApiFormat(data.privacySettings.edit)
        updateData.whoCanJoin = toApiFormat(data.privacySettings.join)
        updateData.whoCanInvite = toApiFormat(data.privacySettings.invite || ["organizers"])
        updateData.whoCanSeeParticipantList = toApiFormat(
          data.privacySettings.seeParticipantList || ["everyone"],
        )
      }

      if (data.type === RitualTypeEnum.SELF_GUIDED_COURSE) {
        updateData.courseModules = courseModules.map(m => ({
          id: m.id?.startsWith("new-") ? undefined : m.id,
          uploadId: m.uploadId,
          title: m.title,
          description: m.description,
          duration: m.duration,
          order: m.order,
          isFree: m.isFree,
          videoFile: m.videoFile,
          thumbnailFile: m.thumbnailFile,
          removeThumbnail: m.removeThumbnail,
        }))
      } else {
        updateData.courseModules = []
      }

      const isSelfGuided =
        data.type === RitualTypeEnum.SELF_GUIDED || data.type === RitualTypeEnum.SELF_GUIDED_COURSE
      const shouldExcludeMinParticipants = isPersonal || isSelfGuided
      updateData.activateMinimumParticipantRequirement = shouldExcludeMinParticipants
        ? false
        : data.minParticipantRequirement
      updateData.minParticipants =
        shouldExcludeMinParticipants || !data.minParticipantRequirement
          ? undefined
          : data.minParticipants
            ? parseInt(data.minParticipants, 10)
            : undefined
      updateData.registrationDeadline =
        shouldExcludeMinParticipants || !data.minParticipantRequirement
          ? undefined
          : data.registrationDeadline || undefined

      if (mediaChanges) {
        const { mediaItems, deletedMediaIds } = mediaChanges
        const files: File[] = []
        const media: import("../services/ritual.service").MediaInput[] = []
        const audioPosters: File[] = []
        const videoThumbnails: File[] = []
        let audioPosterIndex = 0
        let videoThumbnailIndex = 0
        const mediaTitleUpdates: Array<{ id: string; title: string; mediaType: string }> = []

        mediaItems.forEach((item, index) => {
          if (item.uploadId) {
            if (item.mediaType === "VIDEO" || item.mediaType === "AUDIO") {
              const originalUpload = ritual?.uploads?.find(
                (u: RitualUpload) => u.id === item.uploadId,
              )
              const originalTitle = originalUpload?.title || null
              if (item.videoTitle !== originalTitle) {
                mediaTitleUpdates.push({
                  id: item.id,
                  title: item.videoTitle || "",
                  mediaType: item.mediaType,
                })
              }
              if (item.thumbnailFile) {
                const mediaEntry: import("../services/ritual.service").MediaInput & {
                  uploadId?: string
                } = {
                  type: item.mediaType,
                  sortOrder: index,
                  title: item.videoTitle || undefined,
                  uploadId: item.uploadId,
                }
                if (item.mediaType === "VIDEO") {
                  mediaEntry.videoThumbnailIndex = videoThumbnailIndex
                  videoThumbnails.push(item.thumbnailFile)
                  videoThumbnailIndex++
                }
                if (item.mediaType === "AUDIO") {
                  mediaEntry.posterIndex = audioPosterIndex
                  audioPosters.push(item.thumbnailFile)
                  audioPosterIndex++
                }
                media.push(mediaEntry)
              }
            }
            return
          }

          if (item.file) files.push(item.file)
          const mediaEntry: import("../services/ritual.service").MediaInput = {
            type: item.mediaType,
            sortOrder: index,
          }
          if (item.mediaType === "IMAGE") mediaEntry.isCover = item.isCover
          if (item.mediaType === "VIDEO") {
            mediaEntry.title = item.videoTitle || undefined
            if (item.thumbnailFile) {
              mediaEntry.videoThumbnailIndex = videoThumbnailIndex
              videoThumbnails.push(item.thumbnailFile)
              videoThumbnailIndex++
            }
          }
          if (item.mediaType === "AUDIO") {
            mediaEntry.title = item.videoTitle || undefined
            if (item.thumbnailFile) {
              mediaEntry.posterIndex = audioPosterIndex
              audioPosters.push(item.thumbnailFile)
              audioPosterIndex++
            } else {
              mediaEntry.posterIndex = null
            }
          }
          media.push(mediaEntry)
        })

        const coverItem = mediaItems.find(item => item.isCover)

        // Unified reorder: orderedMediaIds spans images/audio (uploadIds) AND
        // native videos (ritual_videos ids), resolved by ritualMediaSection so a
        // single ordered list reorders both tables (#429). Falls back to the
        // media-only order for older payloads.
        const reorderedIds =
          mediaChanges.orderedMediaIds && mediaChanges.orderedMediaIds.length > 0
            ? mediaChanges.orderedMediaIds
            : mediaItems
                .map(item => item.uploadId || item.id)
                .filter(id => id && !id.startsWith("ai-"))

        updateData.mediaChanges = {
          add: {
            media: media.length > 0 ? media : undefined,
            files: files.length > 0 ? files : undefined,
            audioPosters: audioPosters.length > 0 ? audioPosters : undefined,
            videoThumbnails: videoThumbnails.length > 0 ? videoThumbnails : undefined,
          },
          delete: deletedMediaIds.length > 0 ? deletedMediaIds : undefined,
          reorder: reorderedIds.length > 0 ? reorderedIds : undefined,
          setCover:
            coverItem?.uploadId ||
            (coverItem?.id && !coverItem.id.startsWith("ai-") ? coverItem.id : undefined),
        }

        if (mediaTitleUpdates.length > 0) updateData.mediaTitleUpdates = mediaTitleUpdates
      }

      await updateRitualMutation.mutateAsync(updateData)
      setOrganizersToRemove([])
      // Changes are saved -- let the post-save navigation bypass the guard.
      unsavedGuard.allowNavigation()
      navigate(`/rituals/${id}`)
    } catch (err) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      const errorMessage =
        axiosErr?.response?.data?.message ||
        (err instanceof Error ? err.message : t("createRitual.errors.failedToUpdate"))
      toast.error(errorMessage)
      setSubmitError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="size-8 animate-spin text-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-red-500">Failed to load ritual: {(error as Error).message}</p>
      </div>
    )
  }

  return (
    <RitualForm
      mode="edit"
      form={form}
      ritualTypeOptions={RITUAL_TYPE_OPTIONS}
      repeatOptions={repeatOptions}
      participationFeeOptions={participationFeeOptions}
      tags={tags}
      selectedIntentions={selectedIntentions}
      privacySettings={privacySettings}
      organizers={organizers}
      courseModules={courseModules}
      courseVideoAccess={courseVideoAccess}
      onCourseVideoAccessChange={handleCourseVideoAccessChange}
      preservedValues={_preservedValues}
      mediaChanges={mediaChanges}
      initialMedia={initialMedia}
      user={currentUser}
      onTagAdd={handleAddTag}
      onTagRemove={handleRemoveTag}
      onSetPreservedValues={setPreservedValues}
      onCommentChange={handleCommentChange}
      onEditChange={handleEditChange}
      onJoinChange={handleJoinChange}
      onInviteChange={handleInviteChange}
      onSeeParticipantListChange={handleSeeParticipantListChange}
      onMediaChange={handleMediaChange}
      onUploadingChange={setIsVideoUploading}
      onCoverImageChange={handleCoverImageChange}
      onAddModule={handleAddModule}
      onRemoveModule={handleRemoveModule}
      onReorderModules={handleReorderModules}
      onUpdateModule={handleUpdateModule}
      onAddOrganizers={handleAddOrganizers}
      onRemoveOrganizer={() => {}}
      onAddIntention={intention => setSelectedIntentions(prev => [...prev, intention])}
      onRemoveIntention={id => setSelectedIntentions(prev => prev.filter(i => i.id !== id))}
      isSubmitting={isSubmitting}
      submitError={submitError}
      isOrganizerModalOpen={isOrganizerModalOpen}
      setIsOrganizerModalOpen={setIsOrganizerModalOpen}
      onUpdate={async () => {
        await handleUpdate(form.getValues())
      }}
      onCancelRitual={() => setIsCancelDialogOpen(true)}
      onNavigate={path => navigate(path)}
      ritualId={ritual?.id || ""}
      editProps={{
        ritual,
        ritualOrganizers,
        isLoadingOrganizers,
        hasNextPage,
        fetchNextPage,
        organizersToRemove,
        onMarkOrganizerForRemoval: handleMarkOrganizerForRemoval,
        onResendOrganizerInvite: resendOrganizerInvite,
        onWithdrawOrganizerInvite: withdrawOrganizerInvite,
        isResending,
        isWithdrawing,
      }}
      renderModals={() => (
        <>
          <CancelRitualDialog
            isOpen={isCancelDialogOpen}
            onClose={() => setIsCancelDialogOpen(false)}
            ritualId={id || ""}
            ritualTitle={ritual?.title || ""}
          />
          <LeaveConfirmationModal
            isOpen={unsavedGuard.isBlocked}
            onKeepEditing={unsavedGuard.cancelLeave}
            onLeave={unsavedGuard.confirmLeave}
          />
        </>
      )}
    />
  )
}
